import { useState } from "react";
import { Trash2 } from "lucide-react";
import { api } from "../lib/api";
import { MEALS, WD, fdate, run, useLoad } from "../lib/util";
import { Alert, Badge, Btn, Card, Field, Input, Select, SectionTitle, Table } from "../components/kit";
import { useSchool } from "./ctx";

const TYPES = [["feriado", "Feriado"], ["ponto_facultativo", "Ponto facultativo adotado"], ["suspensao", "Suspensão de aulas"], ["sem_atendimento", "Dia sem atendimento"], ["reposicao", "Sábado/dia de reposição"], ["exclusao_refeicao", "Excluir refeições do dia"]];
const TONE = { feriado: "red", ponto_facultativo: "amber", suspensao: "red", sem_atendimento: "muted", reposicao: "green", exclusao_refeicao: "orange" };

function MonthGrid({ month, days, events }) {
  const [y, m] = month.split("-").map(Number);
  const first = new Date(y, m - 1, 1).getDay();
  const n = new Date(y, m, 0).getDate();
  const cells = [...Array(first).fill(null), ...Array.from({ length: n }, (_, i) => i + 1)];
  return (
    <div className="grid grid-cols-7 gap-1.5" data-testid="calendar-grid">
      {WD.map((d) => <div key={d} className="text-[11px] font-semibold text-slate-500 text-center">{d}</div>)}
      {cells.map((d, i) => {
        if (!d) return <div key={i} />;
        const ds = `${month}-${String(d).padStart(2, "0")}`;
        const served = ds in days;
        const ev = events.filter((e) => e.date === ds);
        return (
          <div key={i} data-testid={`cal-day-${ds}`} className={`rounded-lg border p-1.5 min-h-[64px] text-xs ${served ? "bg-white border-slate-200" : "bg-slate-100 border-transparent text-slate-400"}`}>
            <div className={`font-bold ${served ? "text-[#0B192C]" : ""}`}>{d}</div>
            {served && days[ds].length > 0 && <div className="text-[10px] text-orange-700">sem {days[ds].map((x) => MEALS[x]).join(", ")}</div>}
            {ev.map((e) => <div key={e.id} className="truncate text-[10px]" title={e.note}>{TYPES.find((t) => t[0] === e.type)[1]}</div>)}
          </div>
        );
      })}
    </div>
  );
}

export default function CalendarSection() {
  const { month, tick, bump, school, stock } = useSchool();
  const [events, reload] = useLoad(async () => (await api.get("/s/calendar", { params: { month } })).data, [month, tick]);
  const [f, setF] = useState({ date: `${month}-01`, type: "feriado", meals: [], note: "", source: "" });
  const add = async () => {
    const body = { ...f, meals: f.type === "exclusao_refeicao" ? f.meals : [], note: f.note || null, source: f.source || null };
    if (await run(() => api.post("/s/calendar", body), "Evento incluído — distribuição recalculada")) { reload(); bump(); }
  };
  const del = async (e) => (await run(() => api.delete(`/s/calendar/${e.id}`), "Removido")) && (reload(), bump());
  return (
    <div className="space-y-5">
      <SectionTitle title="Calendário escolar" subtitle={`Município: ${school.municipio}/${school.uf}. Segunda a sexta são dias de atendimento por padrão; ajuste abaixo.`} />
      <Alert>O MapIA não cadastra feriados municipais nem pontos facultativos automaticamente. Inclua-os manualmente e informe a fonte (ex.: decreto municipal) para conferência.</Alert>
      <div className="grid lg:grid-cols-[1fr_380px] gap-5">
        <Card>
          <div className="flex justify-between mb-3"><h3 className="font-heading font-bold">Dias do mês</h3><Badge tone="green">{Object.keys(stock?.days || {}).length} dias de atendimento</Badge></div>
          <MonthGrid month={month} days={stock?.days || {}} events={events || []} />
        </Card>
        <Card className="space-y-3">
          <h3 className="font-heading font-bold">Incluir evento</h3>
          <Field label="Data"><Input data-testid="cal-date-input" type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} /></Field>
          <Field label="Tipo"><Select data-testid="cal-type-select" value={f.type} onChange={(e) => setF({ ...f, type: e.target.value })} options={TYPES} /></Field>
          {f.type === "exclusao_refeicao" && (
            <div className="flex flex-wrap gap-3 text-sm">
              {Object.entries(MEALS).map(([k, l]) => <label key={k} className="flex gap-1.5 items-center"><input type="checkbox" data-testid={`cal-meal-${k}`} checked={f.meals.includes(k)} onChange={() => setF({ ...f, meals: f.meals.includes(k) ? f.meals.filter((x) => x !== k) : [...f.meals, k] })} />{l}</label>)}
            </div>
          )}
          <Field label="Descrição"><Input data-testid="cal-note-input" value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} /></Field>
          <Field label="Fonte (decreto, portaria, calendário oficial)"><Input data-testid="cal-source-input" value={f.source} onChange={(e) => setF({ ...f, source: e.target.value })} /></Field>
          <Btn data-testid="cal-add-button" onClick={add}>Incluir</Btn>
        </Card>
      </div>
      <Table testid="calendar-table">
        <thead><tr><th>Data</th><th>Tipo</th><th>Refeições excluídas</th><th>Descrição</th><th>Fonte</th><th /></tr></thead>
        <tbody>
          {(events || []).map((e) => (
            <tr key={e.id}>
              <td>{fdate(e.date)}</td>
              <td><Badge tone={TONE[e.type]}>{TYPES.find((t) => t[0] === e.type)[1]}</Badge></td>
              <td>{e.meals.map((m) => MEALS[m]).join(", ")}</td>
              <td>{e.note}</td>
              <td className="text-xs">{e.source}</td>
              <td><button onClick={() => del(e)}><Trash2 className="w-4 h-4 text-red-500" /></button></td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
}
